import React, { useState, useEffect } from 'react';
import { Button, Icon } from "src/components/Component";
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner, FormGroup, Label, Tooltip } from "reactstrap";
import { Formik } from 'formik';
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import { brandLogoTransformer } from 'src/transformer/transaction-type';
import { Auth } from "aws-amplify";
import Axios from "axios";
import { API_ROUTES } from "src/config/api";
import { useHistory } from 'react-router-dom';
import { convertNumberToPennies } from 'src/utils/money';

import { CheckCircleIcon } from '@heroicons/react/24/outline';

const ScheduledPaymentCreate = ({
  bookingID,
  availableTokens,
  availableTokensLoading,
  triggerReload,
}) => {
  const [modalForm, setModalForm] = useState(false);
  const [activeToken, setActiveToken] = useState(null);
  const [tryAgain, setTryAgain] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const history = useHistory();

  useEffect(() => {
    if (availableTokens.length > 0) {
      setActiveToken(availableTokens[0].id);
    }
  }, [availableTokensLoading, availableTokens]);

  const toggleForm = () => setModalForm(!modalForm);

  if (availableTokensLoading === true || availableTokens.length === 0) return (
    <React.Fragment>
        <Button
          onClick={() => setModalForm(true)}
          id="create-scheduled-payment-button"
          disabled={true}
          className="bg-blue-800 w-100 mb-1 text-white border-none disabled:bg-gray-50 disabler:border-grey-50 disabled:text-slate-800"
        >
          <Icon name="plus" />
          <span>Create Scheduled Payment</span>
        </Button>
        <Tooltip placement="bottom" isOpen={tooltipOpen} target="create-scheduled-payment-button" toggle={() => setTooltipOpen(!tooltipOpen)}>
          There are no tokenised cards on this booking. This is available after a customer has stored their card (from a payment).
        </Tooltip>
    </React.Fragment>
  );

  return (
    <Formik
      initialValues={{
        amount: '',
        picked: null,
        date: '',
      }}
      validationSchema={Yup.object().shape({
        amount: Yup.number().required('Payment amount is required'),
        picked: Yup.string().required('Please pick an option'),
        date: Yup.string().when('picked', {
          is: (picked) => picked === 'later',
          then: Yup.string().required('Please pick a date'),
          otherwise: Yup.string()
        }),
      })}
      onSubmit={async (values, {
        setErrors,
        setStatus,
        setSubmitting,
        resetForm,
        setFieldValue,
      }) => {
        setSubmitting(true);

        const user = await Auth.currentAuthenticatedUser();

        let data = {
          token: activeToken,
          amount: convertNumberToPennies(values.amount),
        };

        if (values.picked === 'later') {
          data.date = values.date;
        }

        await Axios(
          API_ROUTES.payment['booking:payment'](bookingID),
          {
            method: 'post',
            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
            data
          }
        );

        if (tryAgain === false) {
          setModalForm(false);
        }

        setFieldValue('amount', '');
        setFieldValue('picked', null);
        setFieldValue('date', '');

        setSubmitting(false);
        triggerReload();
        setTryAgain(false);
        history.push(`/booking/${bookingID}/scheduled-transactions`);
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <React.Fragment>

          <Button
            onClick={() => setModalForm(true)}
            className="mr-2 bg-blue-800 w-100 mb-1 text-white border-none disabled:bg-gray-50 disabler:border-grey-50 disabled:text-slate-800"
          >
            <Icon name="plus" />
            <span>Create Scheduled Payment</span>
          </Button>

          <Modal isOpen={modalForm} toggle={toggleForm} size="lg">
            <ModalHeader
              toggle={toggleForm}
              className="bg-secondary text-white text-2xl"
              close={
                <button className="close !-mt-[12px]" onClick={toggleForm}>
                  <Icon name="cross" className="text-white mt" />
                </button>
              }
            >
              Create Scheduled Payment
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label htmlFor="default-2" className="form-label text-violet-800 font-bold">
                  Payment amount<small className="text-danger pl-1 pb-3">required</small>
                </Label>
                <div className="form-control-wrap input-group mt-1">
                  <div className="input-group-addon pl-3 pr-3">£</div>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    className={`form-control form-control-xl`}
                    disabled={isSubmitting}
                    value={values.amount}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    min={0}
                    step={0.01}
                    placeholder="e.g. 200.00"
                  />
                </div>
                {typeof errors.amount !== 'undefined' && typeof touched.amount !== 'undefined' && (
                  <small className="text-danger">{errors.amount}</small>
                )}
                {(typeof errors.amount === 'undefined' || typeof touched.amount === 'undefined') && (
                  <span className="form-note">The amount that the customer will pay</span>
                )}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="default-2" className="form-label text-violet-800 font-bold mb-2 block">
                  {
                    availableTokens.length > 1 && (
                      <React.Fragment>
                        Choose payment card<small className="text-danger pl-1 pb-3">required</small>
                      </React.Fragment>
                    )
                  }
                  {
                    availableTokens.length === 1 && (
                      <React.Fragment>
                        Payment card
                      </React.Fragment>
                    )
                  }
                </Label>
                <div className='border border-slate-300 overflow-y-scroll max-h-[220px] rounded-md'>
                  {availableTokensLoading === false && availableTokens.map((token, index) => {
                    const method = brandLogoTransformer(token.card_brand);

                    return (
                      <div key={index}
                        onClick={() => setActiveToken(token.id)}
                        className={`w-100 ${activeToken === token.id ? 'bg-violet-200' : 'hover:bg-violet-100 cursor-pointer'} ${index + 1 < availableTokens.length ? 'border-b border-slate-200' : ''}`}>
                        {activeToken === token.id && (
                          <div className='float-right mt-4 mr-4 bg-violet-600 px-2 py-1 text-sm text-white rounded-md'>
                            <CheckCircleIcon className='h-4 w-4 inline -mt-[4px] mr-[3px]' />
                            Selected
                          </div>
                        )}
                        <div className='flex px-4 py-3'>
                          <div className='flex-initial w-12 text-center'>
                            {method.image ? (
                              <img src={method.image} alt={method.name} className="w-75 h-auto mt-[12px] inline" />
                            ) : (
                              <Icon name={`ni text-primary icon ${method.icon} pr-1 text-5xl`} />
                            )}
                          </div>
                          <div className="flex-initial w-auto pl-4">
                            <div className='font-bold'>{token.cardholder_name}</div>
                            <div className='tracking-wider'>{token?.bin?.substring(0, 4)} {token?.bin?.substring(4, 8)} **** ****</div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </FormGroup>

              <div>
                <Label htmlFor="default-2" className="form-label text-violet-800 font-bold">
                  When should the payment be undertaken?<small className="text-danger pl-1 pb-3">required</small>
                </Label>
                <div className="grid space-y-3 mt-2">
                  <div className="relative flex items-start">
                    <div className="flex items-center h-5 mt-1">
                      <input
                        id="hs-radio-delete"
                        name="picked"
                        type="radio"
                        className="border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-neutral-800 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                        aria-describedby="hs-radio-delete-description"
                        value="now"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        checked={values.picked === 'now'}
                      />
                    </div>
                    <label htmlFor="hs-radio-delete" className="ms-3">
                      <span className="block text-sm font-semibold text-blue-800">
                        Take Payment Now
                      </span>
                      <span
                        id="hs-radio-delete-description"
                        className="block text-xs"
                      >
                        Will take payment now from the customers card.
                      </span>
                    </label>
                  </div>
                  <div className="relative flex items-start">
                    <div className="flex items-center h-5 mt-1">
                      <input
                        id="hs-radio-archive"
                        name="picked"
                        type="radio"
                        value="later"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        checked={values.picked === 'later'}
                        className="border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-neutral-800 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                        aria-describedby="hs-radio-archive-description"
                      />
                    </div>
                    <label htmlFor="hs-radio-archive" className="ms-3">
                      <span className="block text-sm font-semibold text-blue-800">
                        Take Payment Later
                      </span>
                      <span
                        id="hs-radio-archive-description"
                        className="block text-xs"
                      >
                        Take the payment on a specific date of your choosing.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              {
                values.picked === 'later' && (
                  <FormGroup className='mt-3'>
                    <Label htmlFor="default-2" className="form-label text-violet-800 font-bold">
                      Date to execute <small className="text-danger pl-1 pb-3">required</small>
                    </Label>
                    <div className="form-control-wrap">
                      <DatePicker
                        id="departure_date"
                        name="departure_date"
                        showTimeSelect={false}
                        dateFormat="do MMMM yyyy"
                        selected={values.date}
                        onChange={date => setFieldValue('date', date)}
                        onBlur={handleBlur}
                        className={`form-control form-control-xl date-picker ${typeof errors.departure_date !== 'undefined' && typeof touched.departure_date !== 'undefined' ? 'error' : ''}`}
                      />
                    </div>
                    {typeof errors.date !== 'undefined' && typeof touched.date !== 'undefined' && (
                      <small className="text-danger">{errors.date}</small>
                    )}
                    {(typeof errors.date === 'undefined' || typeof touched.date === 'undefined') && (
                      <span className="form-note">The date that the payment will be undertaken</span>
                    )}
                  </FormGroup>
                )
              }

            </ModalBody>
            <ModalFooter className="bg-light !py-4">

              <FormGroup className='!my-0'>
                {
                  values.picked === 'later' && (
                    <Button
                      className="border border-violet-800 text-violet-800 hover:bg-violet-100 mr-2"
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      onClick={() => {
                        setTryAgain(true);
                        handleSubmit();
                      }}
                    >
                      {isSubmitting && tryAgain === true ? <Spinner size="sm" color="dark"> </Spinner> : 'Add Another'}
                    </Button>
                  )}

                <Button
                  className="bg-violet-800 hover:bg-violet-900 text-white"
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting && tryAgain === false ? <Spinner size="sm" color="light"> </Spinner> : 'Create Payment'}
                </Button>
              </FormGroup>
            </ModalFooter>
          </Modal>
        </React.Fragment>
      )}
    </Formik>
  )
};

export default ScheduledPaymentCreate;
